// ── program-form-modal: the create/edit program Modal (POST/PATCH programs) ──
// Wave 2 (Programs). One @vjal/ui Modal that backs both the create flow (POST
// /programs) and the edit flow (PATCH /programs/:id) — the `mode` prop only
// changes the title, the submit label, and the initial values. Presentational +
// validation-only; the server is authoritative (staff/super-admin only for
// mutations — the server re-checks and 403s, which we render honestly inline).
//
// The owning company + assigned coach pickers load from the EXISTING typed
// services (companies.list / users.list) via useHonestQuery, so an unavailable
// option read reads honestly (a disabled select with a hint), never a fabricated
// option. Both are optional on the form: the create body sends only the fields
// the operator filled, and the backend is the source of truth for what's required.
//
// Honest write contract: a server rejection (validation / permission-denied /
// gate-closed / unavailable) is surfaced INLINE via describeProgramWriteError —
// never swallowed, never faked. The caller (the page) owns the actual mutation
// and closes + toasts ONLY after the backend confirms success.

import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Alert, Button, Input, Modal, Select } from "@vjal/ui";

import { useHonestQuery } from "../../components/honest-state";
import { getCoachingServices } from "../../services/coaching-service-provider";
import { coachingQueryKeys } from "../../hooks/coaching-query-keys";
import type {
  CoachingCompanyDto,
  CoachingProgramDto,
  CoachingUserDto,
  ListResponse
} from "../../services/coaching-dtos";
import { PROGRAM_WRITE_STATUSES, describeProgramWriteError, type ProgramWriteStatus } from "./programs-write";

const services = getCoachingServices();

/** Roles that can own a program as its coach (UI hint only; server re-checks). */
const COACH_ROLE = "coach";

export interface ProgramFormValues {
  name: string;
  status: ProgramWriteStatus;
  companyId: string;
  coachId: string;
}

export interface ProgramFormModalProps {
  open: boolean;
  mode: "create" | "edit";
  /** The program being edited (edit mode only) — seeds the initial values. */
  program?: CoachingProgramDto | null;
  onClose: () => void;
  /** The actual write — owned by the page (react-query mutation). */
  onSubmit: (values: ProgramFormValues) => Promise<unknown>;
  submitting?: boolean;
  submitError?: unknown;
}

interface FieldErrors {
  name?: string;
}

function initialValues(mode: "create" | "edit", program?: CoachingProgramDto | null): ProgramFormValues {
  if (mode === "edit" && program) {
    const status = (PROGRAM_WRITE_STATUSES as readonly string[]).includes(program.status ?? "")
      ? (program.status as ProgramWriteStatus)
      : "draft";
    return {
      name: program.name ?? "",
      status,
      companyId: program.company_id ?? "",
      coachId: program.coach_id ?? ""
    };
  }
  return { name: "", status: "draft", companyId: "", coachId: "" };
}

export function ProgramFormModal({
  open,
  mode,
  program,
  onClose,
  onSubmit,
  submitting = false,
  submitError
}: ProgramFormModalProps) {
  const [values, setValues] = useState<ProgramFormValues>(() => initialValues(mode, program));
  const [errors, setErrors] = useState<FieldErrors>({});
  const formId = useId();

  // Option reads (only while the modal is open). Honest: an unavailable read
  // disables the select with a neutral hint, never a fabricated option.
  const companyQuery = useHonestQuery<ListResponse<CoachingCompanyDto>>(
    coachingQueryKeys.list("companies.list"),
    () => services.companies.list(),
    { enabled: open }
  );
  const userQuery = useHonestQuery<ListResponse<CoachingUserDto>>(
    coachingQueryKeys.list("users.list"),
    () => services.users.list(),
    { enabled: open }
  );

  const companyOptions = useMemo(() => companyQuery.data?.items ?? [], [companyQuery.data]);
  const coachOptions = useMemo(
    () => (userQuery.data?.items ?? []).filter((user) => (user.role ?? "") === COACH_ROLE),
    [userQuery.data]
  );

  // Seed ONLY on the open false->true transition (and when the target program
  // identity changes), never on every parent re-render — otherwise an in-progress
  // edit would be clobbered by a background re-render.
  const wasOpen = useRef(false);
  const seededFor = useRef<string | null>(null);
  useEffect(() => {
    const programKey = program?.id ?? null;
    if (open && (!wasOpen.current || seededFor.current !== programKey)) {
      setValues(initialValues(mode, program));
      setErrors({});
      seededFor.current = programKey;
    }
    wasOpen.current = open;
  }, [open, mode, program]);

  const set = <K extends keyof ProgramFormValues>(key: K, value: ProgramFormValues[K]) =>
    setValues((current) => ({ ...current, [key]: value }));

  function validate(): FieldErrors {
    const next: FieldErrors = {};
    if (!values.name.trim()) next.name = "Program name is required.";
    return next;
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    const localErrors = validate();
    if (Object.keys(localErrors).length > 0) {
      setErrors(localErrors);
      return;
    }
    setErrors({});
    try {
      await onSubmit({
        name: values.name.trim(),
        status: values.status,
        companyId: values.companyId,
        coachId: values.coachId
      });
      // The page closes the modal + toasts on success; nothing else to do here.
    } catch {
      // The owning mutation surfaces the error via `submitError`; swallow the
      // re-throw so the modal stays open with the inline error shown.
    }
  }

  const topError = describeProgramWriteError(submitError);
  const companyDisabled = companyQuery.posture !== null && companyQuery.posture !== "empty";
  const coachDisabled = userQuery.posture !== null && userQuery.posture !== "empty";

  return (
    <Modal open={open} onClose={onClose} title={mode === "create" ? "Add program" : "Edit program"}>
      <form id={formId} onSubmit={(event) => void handleSubmit(event)} className="flex flex-col gap-4" noValidate>
        {topError && <Alert variant={topError.tone === "warning" ? "warning" : "error"}>{topError.message}</Alert>}

        <Input
          label="Program name"
          value={values.name}
          onChange={(event) => set("name", event.target.value)}
          error={errors.name}
          required
        />

        <Select
          label="Owning company"
          value={values.companyId}
          disabled={companyDisabled}
          hint={companyDisabled ? "Companies are unavailable right now; you can set this later." : undefined}
          onChange={(event) => set("companyId", event.target.value)}
        >
          <option value="">No company</option>
          {companyOptions.map((company) => (
            <option key={company.id} value={company.id}>
              {company.name || "Unnamed company"}
            </option>
          ))}
        </Select>

        <Select
          label="Assigned coach"
          value={values.coachId}
          disabled={coachDisabled}
          hint={coachDisabled ? "Coaches are unavailable right now; you can set this later." : undefined}
          onChange={(event) => set("coachId", event.target.value)}
        >
          <option value="">No coach</option>
          {coachOptions.map((coach) => (
            <option key={coach.id} value={coach.id}>
              {coach.name || coach.email || "Unnamed coach"}
            </option>
          ))}
        </Select>

        {mode === "edit" && (
          <Select
            label="Lifecycle status"
            value={values.status}
            onChange={(event) => set("status", event.target.value as ProgramWriteStatus)}
            hint="Draft → Active (publish) → Archived. Use the Publish and Archive actions for confirmation steps."
          >
            {PROGRAM_WRITE_STATUSES.map((status) => (
              <option key={status} value={status}>
                {status === "draft" ? "Draft" : status === "active" ? "Active" : "Archived"}
              </option>
            ))}
          </Select>
        )}

        <div className="mt-2 flex items-center justify-end gap-3">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" isLoading={submitting}>
            {mode === "create" ? "Create program" : "Save changes"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
